import React, { Fragment, useEffect, useState } from "react";
import api from "../../api";
import { formatDateDDMMYYYY } from "../../util/formatDate";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import Badge from "../../components/Badge";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
    ReportShell,
    ReportStatCard,
    ReportPanel,
} from "../../components/reports/ReportShell";
import { ReportDateFilterMenu } from "../../components/reports/ReportFilters";

ChartJS.register(
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    ArcElement,
);

const options = [
    { name: "Datos generales", href: "/reports/general-data-report" },
    { name: "Cirugias", href: "/reports/surgery-report" },
    { name: "Estudios", href: "#" },
    { name: "Medicamentos", href: "/reports/medication-report" },
];

const colors = [
    "#0ea5e9",
    "#14b8a6",
    "#f97316",
    "#fb7185",
    "#8b5cf6",
    "#facc15",
    "#38bdf8",
    "#34d399",
];

export default function StudiesPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState();
    const [filters, setFilters] = useState({});
    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();
    const isDesktop = useMediaQuery("(min-width: 1024px)");

    const handleFromDate = (field, value) => setFromDate(value);
    const handleToDate = (field, value) => setToDate(value);

    useEffect(() => {
        if (isDesktop) setSidebarOpen(false);
    }, [isDesktop]);

    useEffect(() => {
        setLoading(true);
        const aux = new URLSearchParams();

        Object.keys(filters || {}).forEach((prop) => {
            const value = filters[prop];
            if (value !== undefined && value !== null) {
                aux.append(prop, value);
            }
        });

        const request =
            aux.toString().length > 0
                ? api.studyReport.getReports(aux)
                : api.studyReport.getReports();

        request
            .then((response) => setData(response))
            .finally(() => setLoading(false));
    }, [filters]);

    const saveFromDate = () => {
        if (fromDate) setFilters((prev) => ({ ...prev, date_after: fromDate }));
    };

    const saveToDate = () => {
        if (toDate) setFilters((prev) => ({ ...prev, date_before: toDate }));
    };
    const applyDateFilters = () => {
        saveToDate();
        saveFromDate();
    };

    const deleteFromDate = () => {
        const state = { ...filters };
        delete state.date_after;
        setFilters(state);
        setFromDate(undefined);
    };

    const deleteToDate = () => {
        const state = { ...filters };
        delete state.date_before;
        setFilters(state);
        setToDate(undefined);
    };

    const deleteDates = () => {
        const state = { ...filters };
        delete state.date_after;
        delete state.date_before;
        setFilters(state);
        setFromDate(undefined);
        setToDate(undefined);
    };

    const clinicalData = data
        ? {
              labels: data.clinicalStudies.data
                  .filter((element) => element.quantity > 0)
                  .map((element) => element.name),
              datasets: [
                  {
                      data: data.clinicalStudies.data
                          .filter((element) => element.quantity > 0)
                          .map((element) => element.quantity),
                      backgroundColor: colors,
                  },
              ],
          }
        : undefined;

    const ophthalmologicalData = data
        ? {
              labels: data.ophthalmologicalStudies.data
                  .filter((element) => element.quantity > 0)
                  .map((element) => element.name),
              datasets: [
                  {
                      data: data.ophthalmologicalStudies.data
                          .filter((element) => element.quantity > 0)
                          .map((element) => element.quantity),
                      backgroundColor: colors,
                  },
              ],
          }
        : undefined;

    const totalClinical = data?.clinicalStudies?.quantity || 0;
    const totalOphthalmological = data?.ophthalmologicalStudies?.quantity || 0;
    const totalStudies = totalClinical + totalOphthalmological;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: isDesktop ? "left" : "bottom",
            },
        },
    };

    return (
        <ReportShell
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            setSidebarOpenDesktop={setSidebarOpenDesktop}
            isDesktop={isDesktop}
            loading={loading}
            currentLabel="Estudios"
            options={options}
            title="Distribucion y volumen de estudios"
            actions={
                <ReportDateFilterMenu
                    handleFromDate={handleFromDate}
                    handleToDate={handleToDate}
                    onClear={deleteDates}
                    onApply={applyDateFilters}
                />
            }
            badges={
                <>
                    {filters.date_after && <Badge text={`Desde: ${formatDateDDMMYYYY(filters.date_after)}`} handleDelete={deleteFromDate} />}
                    {filters.date_before && <Badge text={`Hasta: ${formatDateDDMMYYYY(filters.date_before)}`} handleDelete={deleteToDate} />}
                </>
            }
            summary={[
                <ReportStatCard key="total" label="Total de estudios" value={totalStudies} tone="blue" />,
                <ReportStatCard key="clinical" label="Estudios clinicos" value={totalClinical} tone="emerald" />,
                <ReportStatCard key="oph" label="Estudios oftalmologicos" value={totalOphthalmological} tone="violet" />,
                <ReportStatCard
                    key="ratio"
                    label="Peso de estudios clinicos"
                    value={totalStudies ? `${Math.round((totalClinical / totalStudies) * 100)}%` : "0%"}
                    tone="amber"
                />,
            ]}
        >
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <ReportPanel title="Estudios clinicos">
                    {clinicalData ? (
                        <div className="h-[340px] sm:h-[420px]">
                            <Pie options={chartOptions} data={clinicalData} />
                        </div>
                    ) : null}
                </ReportPanel>

                <ReportPanel title="Estudios oftalmologicos">
                    {ophthalmologicalData ? (
                        <div className="h-[340px] sm:h-[420px]">
                            <Pie options={chartOptions} data={ophthalmologicalData} />
                        </div>
                    ) : null}
                </ReportPanel>
            </div>
        </ReportShell>
    );
}